import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  SimpleGrid,
  Badge,
  Select,
  HStack,
  Button,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { CalendarIcon, AddIcon } from '@chakra-ui/icons'; // Replace CalendarPlusIcon with AddIcon in the imports
import Papa from 'papaparse';
import { motion } from 'framer-motion';

interface EarningsEvent {
  symbol: string;
  name: string;
  reportDate: string;
  fiscalDateEnding: string;
  estimate: string;
  currency: string;
}

interface IPOEvent {
  symbol: string;
  name: string;
  ipoDate: string;
  priceRange: string;
  exchange: string;
  shares: string;
}

interface CalendarEventProps {
  date: string;
  events: (EarningsEvent | IPOEvent)[];
}

interface EventRowProps {
  event: EarningsEvent | IPOEvent;
}

// Memoize the CalendarEvent component to prevent unnecessary re-renders
const CalendarEvent = memo<CalendarEventProps>(({ date, events }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const isToday = date === today;

  const formattedDate = useMemo(() => {
    return new Date(date).toLocaleDateString(undefined, { 
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }, [date]);

  return (
    <Box
      p={4}
      bg={bgColor}
      borderRadius="lg"
      boxShadow="md"
      border="1px"
      borderColor={isToday ? 'blue.400' : useColorModeValue('gray.200', 'gray.700')}
      position="relative"
      _hover={{ transform: 'translateY(-2px)', transition: 'transform 0.2s' }}
    >
      {isToday && (
        <Badge
          position="absolute"
          top="-2"
          right="-2"
          colorScheme="blue"
          borderRadius="full"
          px={2}
        >
          Today
        </Badge>
      )}
      
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between">
          <Text fontWeight="bold" fontSize="lg">
            {formattedDate}
          </Text>
          <Badge colorScheme="purple">{events.length} Events</Badge>
        </HStack>
        
        <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto">
          {events.map((event, idx) => (
            <EventRow key={`${event.symbol}-${idx}`} event={event} />
          ))}
        </VStack>
      </VStack>
    </Box>
  );
});

// Define display name for memoized components
CalendarEvent.displayName = 'CalendarEvent';

// Separate EventRow component to further optimize rendering
const EventRow = memo<EventRowProps>(({ event }) => {
  const bgColor = useColorModeValue('gray.50', 'gray.700');

  return (
    <Box
      p={2}
      borderRadius="md"
      bg={bgColor}
    >
      <HStack justify="space-between">
        <VStack align="start" spacing={0}>
          <Text fontWeight="bold">{event.symbol}</Text>
          <Text fontSize="sm" noOfLines={1}>{event.name}</Text>
        </VStack>
        {'estimate' in event ? (
          <Badge colorScheme="green">
            Est: {parseFloat(event.estimate).toFixed(2)}
          </Badge>
        ) : (
          <Badge colorScheme="blue">
            {event.priceRange}
          </Badge>
        )}
        <Button
          size="sm"
          leftIcon={<AddIcon />} // Use AddIcon instead of CalendarPlusIcon
          onClick={() => handleSaveToCalendar(event)}
        >
          Save
        </Button>
      </HStack>
    </Box>
  );
});

// Define display name for memoized components
EventRow.displayName = 'EventRow';

// Add the handleSaveToCalendar function
const handleSaveToCalendar = (event: EarningsEvent | IPOEvent) => {
  const title = 'Earnings Report';
  const description = `Event for ${event.symbol} - ${event.name}`;
  const date = 'reportDate' in event ? event.reportDate : event.ipoDate;
  const start = new Date(date).toISOString();
  const end = new Date(new Date(date).getTime() + 60 * 60 * 1000).toISOString(); // 1 hour duration

  const googleCalendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${start.replace(/-|:|\.\d+/g, '')}/${end.replace(/-|:|\.\d+/g, '')}&details=${encodeURIComponent(description)}&sf=true&output=xml`;

  window.open(googleCalendarUrl, '_blank');
};

export const EarningsCalendar: React.FC = () => {
  const [horizon, setHorizon] = useState<'3month' | '6month' | '12month'>('3month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [earningsData, setEarningsData] = useState<Record<string, EarningsEvent[]>>({});
  const [ipoData, setIpoData] = useState<Record<string, IPOEvent[]>>({});
  const [activeTab, setActiveTab] = useState(0);

  // Memoize the data processing functions
  const processCalendarData = useCallback((csvData: any[]) => {
    return csvData.reduce((acc: Record<string, any[]>, event: any) => {
      const date = event.reportDate || event.ipoDate;
      // Skip invalid or empty date fields
      if (!date || isNaN(Date.parse(date))) {
        return acc;
      }
      if (!acc[date]) acc[date] = [];
      acc[date].push(event);
      return acc;
    }, {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [earningsResponse, ipoResponse] = await Promise.all([
        fetch(
          `https://www.alphavantage.co/query?function=EARNINGS_CALENDAR&horizon=${horizon}&apikey=Q8ZI5UJX6WTQ6KW0`
        ),
        fetch(
          `https://www.alphavantage.co/query?function=IPO_CALENDAR&apikey=Q8ZI5UJX6WTQ6KW0`
        )
      ]);

      const [earningsText, ipoText] = await Promise.all([
        earningsResponse.text(),
        ipoResponse.text()
      ]);

      // Parse CSV data in parallel
      const [earningsResults, ipoResults] = await Promise.all([
        new Promise<(EarningsEvent | IPOEvent)[]>(resolve => {
          Papa.parse(earningsText, {
            header: true,
            complete: (results) => resolve(results.data as (EarningsEvent | IPOEvent)[])
          });
        }),
        new Promise<(EarningsEvent | IPOEvent)[]>(resolve => {
          Papa.parse(ipoText, {
            header: true,
            complete: (results) => resolve(results.data as (EarningsEvent | IPOEvent)[])
          });
        })
      ]);

      setEarningsData(processCalendarData(earningsResults));
      setIpoData(processCalendarData(ipoResults));
    } catch (err) {
      setError('Failed to fetch calendar data. Please try again later.');
      console.error('Calendar fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [horizon, processCalendarData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Memoize the calendar rendering function
  const renderCalendar = useCallback((data: Record<string, any[]>) => {
    const dates = Object.keys(data).sort();
    
    return (
      <SimpleGrid 
        columns={{ base: 1, md: 2, lg: 3, xl: 4 }} 
        spacing={4}
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ type: 'tween', duration: 0.3 }}
        sx={{
          '& > *:last-child:nth-child(odd)': {
            gridColumn: {
              md: dates.length % 2 === 1 ? '1 / -1' : 'auto',
              lg: dates.length % 3 === 1 ? '1 / -1' : 'auto',
              xl: dates.length % 4 === 1 ? '1 / -1' : 'auto'
            }
          },
          '& > *:nth-last-child(2):nth-child(odd)': {
            gridColumn: {
              lg: dates.length % 3 === 2 ? 'span 2' : 'auto',
              xl: dates.length % 4 === 2 ? 'span 2' : 'auto'
            }
          }
        }}
      >
        {dates.map(date => (
          <CalendarEvent key={date} date={date} events={data[date]} />
        ))}
      </SimpleGrid>
    );
  }, []);

  // Memoize the tab content to prevent unnecessary re-renders
  const tabContent = useMemo(() => (
    <Tabs isFitted variant="enclosed" index={activeTab} onChange={setActiveTab}>
      <TabList mb="1em">
        <Tab>Earnings Calendar</Tab>
        <Tab>IPO Calendar</Tab>
      </TabList>
      <TabPanels>
        <TabPanel>
          {loading ? (
            <Box textAlign="center" py={8}>
              <Spinner size="xl" />
            </Box>
          ) : Object.keys(earningsData).length > 0 ? (
            renderCalendar(earningsData)
          ) : (
            <Text textAlign="center" color="gray.500">
              No earnings events scheduled for this period.
            </Text>
          )}
        </TabPanel>
        <TabPanel>
          {loading ? (
            <Box textAlign="center" py={8}>
              <Spinner size="xl" />
            </Box>
          ) : Object.keys(ipoData).length > 0 ? (
            renderCalendar(ipoData)
          ) : (
            <Text textAlign="center" color="gray.500">
              No IPO events scheduled for this period
            </Text>
          )}
        </TabPanel>
      </TabPanels>
    </Tabs>
  ), [loading, earningsData, ipoData, activeTab, renderCalendar]);

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" wrap="wrap" spacing={4}>
          <Heading size="lg">Financial Calendar</Heading>
          <HStack spacing={4}>
            <Select
              value={horizon}
              onChange={(e) => setHorizon(e.target.value as any)}
              width="auto"
            >
              <option value="3month">3 Months</option>
              <option value="6month">6 Months</option>
              <option value="12month">12 Months</option>
            </Select>
            <Button
              leftIcon={<CalendarIcon />}
              colorScheme="blue"
              onClick={fetchData}
              isLoading={loading}
            >
              Refresh
            </Button>
          </HStack>
        </HStack>

        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {tabContent}
      </VStack>
    </Container>
  );
};
